/** Tipos do contrato docs/API.md (v0.1). Dinheiro viaja como string de centavos. */

export type OrderState =
  | "quoted"
  | "awaiting_funds"
  | "funds_received"
  | "executing"
  | "sent"
  | "settled"
  | "failed"
  | "refunded"
  | "expired"
  | "under_review";

export const ORDER_STATES: OrderState[] = [
  "quoted", "awaiting_funds", "funds_received", "executing", "sent", "settled", "failed", "refunded", "expired", "under_review",
];
export const TERMINAL_STATES: ReadonlySet<OrderState> = new Set(["settled", "refunded", "expired"]);

export type OrderEventType =
  | "quote_locked" | "pix_charge_created" | "pix_received" | "pix_divergent" | "quote_expired" | "cancelled"
  | "execution_started" | "execution_sent" | "execution_settled" | "execution_failed" | "refund_completed"
  | "review_opened" | "review_approved" | "review_rejected" | "late_pix_received"
  | "partner_stuck" | "refund_manual_required";

export type ActorKind = "client_user" | "operator" | "system" | "partner_vasp" | "partner_pix" | "partner_kyb";

export type CompanyStatus = "draft" | "submitted" | "in_review" | "approved" | "rejected" | "needs_more_info" | "blocked";
export type CompanyRole = "owner" | "finance" | "viewer";
export type StaffRole = "admin" | "ops" | "compliance" | "readonly";
export type BeneficiaryStatus = "pending_review" | "active" | "blocked";
export type Nature = "goods_import" | "service_import";
export type Relationship = "goods" | "service";

export interface ApiErrorBody {
  error: { code: string; message: string; details?: unknown };
}

export interface LegalTexts {
  partnerDisclosure?: string;
  termsVersion?: string;
  terms?: string;
  bankDisclaimer?: string;
}

export interface Me {
  user: { id: string; email: string; name: string; staffRole?: StaffRole | null };
  company?: {
    id: string;
    cnpj: string;
    legalName: string;
    tradeName?: string | null;
    cnae?: string | null;
    receitaStatus?: string | null;
    status: CompanyStatus;
    limits: { perTxCents: string; monthlyCents: string };
    monthUsedCents: string;
  } | null;
  role?: CompanyRole | null;
  sandbox?: boolean;
  featureFlags?: { sandboxPanel?: boolean; bankRail?: boolean } | null;
  legalTexts?: LegalTexts | null;
}

export interface LoginResponse {
  step: "totp_verify" | "totp_enroll";
  pendingToken: string;
  otpauthUrl?: string;
  secret?: string;
}

export interface Company {
  id: string;
  cnpj: string;
  legalName: string;
  tradeName?: string | null;
  cnae?: string | null;
  receitaStatus?: string | null;
  status: CompanyStatus;
  activity?: Activity | null;
  address?: PostalAddress | null;
  phone?: string | null;
  website?: string | null;
  companyType?: string | null;
  annualRevenueUsdCents?: string | null;
  termsVersion?: string | null;
  perTxLimitCents?: string;
  monthlyLimitCents?: string;
  takeRateOverrideBp?: number | null;
  betaEndsAt?: string | null;
  blockedAt?: string | null;
  createdAt?: string;
  incorporationDate?: string | null;
}

export interface Activity {
  description: string;
  products: string;
  countries: string[];
  monthlyVolumeUsdCents: string;
}

export interface PostalAddress { line1: string; city: string; state: string; postalCode: string; country?: string }
export interface Ubo { name: string; cpf: string; sharePct: number; birthDate?: string; email?: string; address?: PostalAddress }

export type CompanyDocKind = "contrato_social" | "comprovante_endereco" | "documento_socio" | "documento_socio_verso" | "outro";

export interface DocumentRef {
  id: string;
  kind: string;
  fileName: string;
  mimeType?: string;
  sizeBytes?: number;
  createdAt?: string;
  createdById?: string | null;
}

export interface KybCase {
  id: string;
  companyId?: string;
  status: CompanyStatus;
  flags?: string[];
  reasons?: string[];
  decisionNote?: string | null;
  submittedAt?: string;
  decidedAt?: string | null;
  /** Links de verificação web do parceiro (Avenia KYB Level 1). */
  partnerUrls?: { label: string; url: string }[];
  company?: { id?: string; legalName?: string; cnpj?: string } | null;
}

export interface ApiKey {
  id: string;
  name: string;
  prefix: string;
  createdAt: string;
  lastUsedAt?: string | null;
  revokedAt?: string | null;
}

export interface CompanyUser {
  id?: string;
  userId?: string;
  email: string;
  name: string;
  role: CompanyRole;
  createdAt?: string;
}

export type BankScheme = "ach" | "fedwire" | "swift" | "cnaps";
export type SettlementRail =
  | { kind: "stablecoin"; asset: "USDT" | "USDC"; network: "tron" | "ethereum" | "polygon" | "solana"; address: string }
  | { kind: "bank"; country: string; scheme: BankScheme; account: Record<string, string> };

export interface Beneficiary {
  id: string;
  companyId?: string;
  legalName: string;
  country: string;
  address?: string | null;
  document?: string | null;
  relationship: Relationship;
  rail: SettlementRail;
  status: BeneficiaryStatus;
  screeningFlags?: string[];
  paymentsCount?: number;
  lastPaidAt?: string | null;
  dataChangedAt?: string | null;
  createdAt?: string;
  company?: { id?: string; legalName?: string } | null;
}

export interface NetworkInfo {
  network: string;
  asset?: string;
  assets?: string[];
  feeCents?: string;
  label?: string;
  enabled?: boolean;
}

export interface Quote {
  id: string;
  usdCents: string;
  rateE8: string;
  baseCents: string;
  /** Plano de preço v3 e a taxa que ele promete (bp). */
  pricePlan?: "table" | "beta" | "custom" | null;
  planFeeBp?: number | null;
  /** Taxa FoxyPay: conversão e rede do parceiro dentro. */
  feeBp: number;
  feeCents: string;
  /** Tarifa de destino: só conta em dólar; na carteira vem incluída. */
  destinationCents: string;
  destinationIncluded: boolean;
  iofBp: number;
  iofCents: string;
  totalCents: string;
  vetE8?: string;
  ptaxE8?: string | null;
  ptaxAt?: string | null;
  bank?: { totalCents: string; savingsCents: string; vetE8?: string; disclaimer?: string } | null;
  /** Só na Toca: divisão interna (parte do parceiro, nossa parte, rede). */
  split?: { partnerSpreadBp: number; partnerSpreadCents: string; takeRateBp: number; takeRateCents: string; networkFeeCents: string };
  expiresAt: string;
  nature?: Nature;
  beneficiaryId?: string;
  createdAt?: string;
}

export interface PixCharge {
  txid: string;
  qrCodePayload: string;
  amountCents: string;
  expiresAt: string;
  paidCents?: string | null;
  endToEndId?: string | null;
  status?: string;
  paidAt?: string | null;
  raw?: unknown;
}

export interface Execution {
  status: string;
  txHash?: string | null;
  bankRef?: string | null;
  fxContractRef?: string | null;
  failureCode?: string | null;
  executionId?: string;
  raw?: unknown;
}

export interface OrderEvent {
  seq: number;
  type: OrderEventType | string;
  fromState?: OrderState | null;
  toState: OrderState;
  actorKind: ActorKind | string;
  actorId?: string | null;
  at: string;
  payload?: Record<string, unknown> | null;
}

export interface OrderSummary {
  id: string;
  state: OrderState;
  stateLabel?: string;
  usdCents: string;
  totalCents: string;
  reference?: string | null;
  reviewReasons?: string[];
  createdAt: string;
  settledAt?: string | null;
  nps?: number | null;
  beneficiaryId?: string;
  beneficiary?: Pick<Beneficiary, "id" | "legalName" | "country" | "rail"> | null;
  beneficiaryName?: string;
  companyId?: string;
  company?: { id?: string; legalName?: string } | null;
  quote?: Partial<Quote> | null;
  savingsCents?: string | null;
  pixCharge?: Partial<PixCharge> | null;
  execution?: Partial<Execution> | null;
  documents?: DocumentRef[];
}

/** GET /orders/stats: KPIs do mês calculados no servidor (calendário America/Sao_Paulo). */
export interface OrderStats {
  month: { from: string; to: string };
  settledCount: number; paidBrlCents: string; paidUsdCents: string; savingsCents: string;
  sendingCount: number; reviewCount: number;
  limits: { perTxCents: string; monthlyCents: string }; monthUsedCents: string; availableCents: string;
}

export interface OrderDetail {
  order: OrderSummary;
  beneficiary: Beneficiary | null;
  quote: Quote;
  events: OrderEvent[];
  pixCharge?: PixCharge | null;
  execution?: Execution | null;
  documents: DocumentRef[];
}

export interface LedgerLine { account: string; debitCents: string; creditCents: string }
export interface LedgerEntry { id: string; kind: string; postedAt: string; lines: LedgerLine[] }
export interface Note { id: string; authorId?: string; authorName?: string; body: string; createdAt: string }

export interface AdminOrderDetail extends OrderDetail {
  company?: { id: string; legalName: string; cnpj?: string } | null;
  ledger?: { entries: LedgerEntry[]; balances: Record<string, string> } | LedgerEntry[]; // a API devolve { entries, balances }; array só em payloads antigos
  notes?: Note[];
  partnerPayloads?: unknown;
  webhooks?: unknown[];
}

export interface Page<T> { items: T[]; total: number; page?: number; pageSize?: number }

export interface Notification {
  id: string;
  subject: string;
  body: string;
  orderId?: string | null;
  readAt?: string | null;
  createdAt: string;
}

export interface AdminDashboard {
  tpvCents: string;
  ordersCount: number;
  avgTicketCents: string;
  revenueCents: string;
  effectiveTakeRateBp: number;
  timing: {
    quoteToFundsP50Sec: number;
    quoteToFundsP95Sec: number;
    fundsToSettledP50Sec: number;
    fundsToSettledP95Sec: number;
  };
  exceptionRate: number;
  expiredRate: number;
  funnel: { registered: number; kybApproved: number; firstPayment: number; secondPayment: number };
  costPerOrderCents: string;
  marginPerOrderCents: string;
  pendingAccess?: number;
  /** On-ramps com Pix no parceiro há mais de 30 min e pedido ainda em awaiting_funds. */
  stuckOnramps?: { count: number; items: { orderId: string; minutes: number; totalCents: string }[] };
  openExceptions?: number;
  reconciliation?: { day?: string; issueCount?: number; finishedAt?: string | null } | null;
}

export interface ReconciliationRun {
  id: string;
  day: string;
  startedAt: string;
  finishedAt?: string | null;
  pixCount: number;
  pixCents: string;
  orderCount: number;
  orderCents: string;
  execCount: number;
  feesCents: string;
  revenueCents: string;
  balances?: Record<string, string> | null;
  issueCount: number;
}

export interface ReconciliationIssue {
  id: string;
  runId: string;
  orderId?: string | null;
  kind: string;
  detail: unknown;
  resolvedAt?: string | null;
  resolution?: string | null;
  createdAt: string;
}

export type SettingKey = "fee_schedule" | "tax_rules" | "networks" | "bank_comparison" | "legal_texts" | "feature_flags" | "review_rules";
export const SETTING_KEYS: SettingKey[] = ["fee_schedule", "tax_rules", "bank_comparison", "review_rules", "legal_texts", "feature_flags", "networks"];

export interface Setting {
  key: SettingKey;
  version: number;
  value: unknown;
  history: { version: number; createdAt: string; note?: string | null }[];
}

export interface AuditEntry {
  id: string;
  at: string;
  actorId?: string | null;
  actorKind: string;
  action: string;
  target?: string | null;
  metadata?: unknown;
  ip?: string | null;
}

export type AdminOrderAction = "reprocess" | "start_refund" | "add_note" | "approve_review" | "reject_review" | "confirm_manual_refund";

/** Motivo de revisão: o parceiro não devolve o Pix sozinho; a Toca devolve no dashboard e confirma com `confirm_manual_refund`. */
export const REFUND_MANUAL_REASON = "refund_manual";
/** On-ramp `funded` há mais que isto sem a conversão terminar é "parado" (mesmo valor de STUCK_AFTER_MS na API). */
export const STUCK_AFTER_MS = 30 * 60_000;

/** Cadastro aguardando liberação (beta fechado). */
export interface AccessRequest {
  id: string;
  email: string;
  name: string;
  access: "pending" | "approved" | "rejected";
  createdAt: string;
  accessDecidedAt: string | null;
  accessNote: string | null;
  company: { id: string; legalName: string; cnpj: string } | null;
}

/** Lista de espera do site institucional. */
export interface Lead {
  id: string;
  email: string;
  cnpj: string;
  source: string;
  createdAt: string;
  contactedAt: string | null;
  note: string | null;
  answers: Record<string, string> | null;
}
